import { ProductService } from '@services/ProductService'

import type { Product } from '../../../../../types'
import { normalize } from '../../../../../utils/normalize'

import { type MessageType, type BatchRow } from './useBatch'

type SubscriptionCsvRow = {
    schoolName: string
    adminName?: string
    seats: string
    adminEmails: string
    products: string
    domain?: string
    keyword?: string
    govId?: string
    country?: string
} & {
    [key: string]: 'TRUE' | 'FALSE'
}

const validateEmail = (email: string) => {
    const regexp =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

    return email.match(regexp)
}

const validateSeats = (seats: string) => {
    // only checking that the "seats" string is a whole number
    return /^-?\d+$/.test(seats)
}

export const transformHeader = (header: string) => {
    if (header.toLowerCase().match(/emails/)) {
        return 'adminEmails'
    }
    if (header.toLowerCase().match(/seats/)) {
        return 'seats'
    }
    if (header.toLowerCase().match(/post/)) {
        return 'postalCode'
    }
    if (header.toLowerCase().match(/country/)) {
        return 'country'
    }
    if (header.toLowerCase().match(/school/)) {
        return 'schoolName'
    }
    if (header.toLowerCase().match(/domain/)) {
        return 'domain'
    }
    if (header.toLowerCase().match(/govId/)) {
        return 'govId'
    }
    if (header.toLowerCase().match(/keyword/)) {
        return 'keyword'
    }

    return header
}

export const validateAndFormatResults = async (
    results: Papa.ParseResult<unknown>
): Promise<{
    data: BatchRow[]
    isValid: boolean
    fields: string[]
}> => {
    // Setup fields that are products
    const availableProducts: Product[] = []

    const retrievedProducts = await ProductService.list()

    const normalizedProduct = normalize(retrievedProducts, 'key')

    const { fields } = results.meta

    if (!fields) {
        throw new Error('No fields set')
    }

    fields.forEach(field => {
        if (normalizedProduct[field]) {
            availableProducts.push(normalizedProduct[field])
        }
    })

    let validityTracker = true
    const data = results.data as SubscriptionCsvRow[]

    // Format and validate data
    const mapped = data.map((row: SubscriptionCsvRow): BatchRow => {
        const errorMessages: { type: MessageType; message: string }[] = []
        const { schoolName, seats, adminEmails, postalCode, domain, keyword, govId, country } = row

        // only use true products
        const mappedProducts = availableProducts.filter(product => {
            if (row[product.key] === 'TRUE') {
                return true
            }

            if (parseInt(row[product.key], 10) > 0) {
                return true
            }

            return false
        })

        // @ts-ignore filter out duplicate products - this is the easiest way
        const uniqueProducts = [...new Set(mappedProducts)]

        if (uniqueProducts.length <= 0) {
            errorMessages.push({
                message: `No products in for ${schoolName.trim()}`,
                type: 'ERROR'
            })
        }

        const emailsList = adminEmails.split(';').map(e => e.trim().toLowerCase())
        const areEmailsValid: boolean = emailsList.reduce((collector: boolean, email) => {
            return collector && !!validateEmail(email)
        }, true)

        const isValidSeat = validateSeats(seats.trim())

        const isValid = !!(areEmailsValid && isValidSeat)

        validityTracker = !!(validityTracker && isValid)

        const status = isValid ? 'DRAFT' : 'INVALID'

        if (!isValidSeat) {
            errorMessages.push({
                message: `${schoolName.trim()} has an invalid seat number :: ${seats}`,
                type: 'ERROR'
            })
        }

        if (!areEmailsValid) {
            errorMessages.push({
                message: `${schoolName.trim()} has an invalid email address`,
                type: 'ERROR'
            })
        }

        let passedDomain = ''
        if (domain) {
            passedDomain = domain?.trim().toLowerCase()
        } else if (areEmailsValid && emailsList.length > 0) {
            passedDomain = emailsList[0].trim().toLocaleLowerCase().split('@')[1]
        }

        return {
            values: {
                schoolName: schoolName.trim(),
                seats: parseInt(seats, 10),
                adminEmails: emailsList,
                postalCode: postalCode.trim(),
                products: uniqueProducts,
                domain: passedDomain,
                keyword: keyword?.trim(),
                govId: govId ? govId.trim() : undefined,
                country: country ? country.trim() : undefined
            },
            status,
            isValid,
            errors: errorMessages,
            actionType: null
        }
    })

    return {
        data: mapped,
        isValid: validityTracker,
        fields
    }
}
